import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { martinaEmailDraftApproval, type MartinaEmailComposerReview, type MartinaEmailComposerReviewReader, type MartinaEmailComposerApprovedSend } from "@shared/martinaEmailDraftApproval";
import { useLocation } from "wouter";
import { AlertTriangle, ArrowUpRight, CalendarDays, Check, ExternalLink, Globe2, ListFilter, Loader2, Mic, RefreshCw, Send, Sparkles, Square, ThumbsDown, Unlink, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { apiRequest, extractApiErrorDetails } from "@/lib/queryClient";
import { martinaUiErrorMessage } from "@/components/exai/martinaUiError";
import {
  MARTINA_RUN_DELIVERY_RECONNECT_REQUIRED,
  MARTINA_RUN_OBSERVE_BUDGET_EXCEEDED,
  persistentRunRecoveryMessage,
  shouldClearPersistentRunPointer,
  shouldInvalidatePersistentRun,
} from "@/components/exai/persistentRunRecovery";
import {
  isMartinaRunTerminalStatus,
  resolveMartinaRunObserveUntil,
} from "@/components/exai/martinaRunObserve";
import {
  isSavedUserTurnForRequest,
  planMartinaTurnSend,
  shouldResumeAcceptedTurnAfterPostFailure,
} from "@/components/exai/martinaTurnSendRecovery";
import { withReturnTo } from "@/lib/emailLinks";
import { memorySourcesForMessage, webSourcesForDisplay } from "./martinaMessageSources";
import { MartinaMarkdown } from "./MartinaMarkdown";
export { normalizeLegacyMartinaAnswer } from "./MartinaMarkdown";
import { canonicalEmailSendFeedback, type CanonicalEmailSendResult } from "@/lib/emailCanonicalSend";
import { useVoiceRecognition } from "@/hooks/useVoiceRecognition";
import { MartinaAvatar } from "@/components/exai/MartinaAvatar";
import { MartinaComposerInlineField } from "@/components/exai/MartinaComposerInlineField";
import { MartinaThinkingIndicator } from "@/components/exai/MartinaThinkingIndicator";
import { useMartinaOperatorNotificationCount } from "@/hooks/useMartinaOperatorNotificationCount";
import { martinaCrmQueryViewPath, type MartinaCrmQueryView } from "@/lib/martinaCrmQueryView";
import { interactiveTurnDeadlineAt } from "@shared/martinaTurnBudget";
import {
  applyUniqueMentionToSegments,
  activeComposerAtQuery,
  activeComposerPlainNameQuery,
  composerMentionDisambiguationLabel,
  composerSegmentsFromPlainText,
  decideVoiceAutoSend,
  displayComposerText,
  primaryComposerNameQuery,
  resolveComposerMentionUniqueness,
  serializeComposerPayload,
  stripComposerTransportForDisplay,
  uniquenessFromSelectedComposerHit,
  type MartinaComposerCrmHit,
  type MartinaComposerSegment,
} from "@/components/exai/martinaComposerMentionContract";
import { fetchComposerMentionHits } from "@/components/exai/fetchComposerMentionHits";

/**
 * Bug reale 25 lug 2026: dopo "Conferma e crea" appariva solo "Applicato nel
 * CRM", senza nessun modo di raggiungere il record appena creato — Michele
 * doveva cercarlo a mano. Il backend restituisce gia' `block.entity` (mai
 * usato lato client). Route coerenti con quelle usate altrove nel CRM
 * (promemoria task, link meeting/azienda/deal).
 */
function appliedEntityRoute(entity: { type?: string; id?: number } | null | undefined): string | null {
  if (!entity?.type || !Number.isFinite(entity.id)) return null;
  switch (entity.type) {
    case "file": return `/files?fileId=${entity.id}`;
    case "task": return `/tasks?taskId=${entity.id}`;
    case "meeting": return `/meetings/${entity.id}`;
    case "company": return `/companies/${entity.id}`;
    case "deal": return `/deals/${entity.id}`;
    case "contact": return `/contacts/${entity.id}`;
    case "lead": return `/leads/${entity.id}`;
    default: return null;
  }
}

/**
 * Una sola verità in chat: se la card è già «Applicato», non mostrare il
 * testo pending «Non è ancora stato creato…» (messaggi storici o race di sync).
 */
function assistantPlainTextForDisplay(message: {
  plainText: string;
  contentBlocks?: Array<{ type?: string; status?: string; proposal?: { type?: string; title?: string; name?: string; lane?: string } }>;
}): string {
  const applied = message.contentBlocks?.find((block) => block.type === "proposal" && block.status === "applied");
  const text = message.plainText ?? "";
  if (!applied?.proposal || !/Non è ancora|usa\s+[“"]Conferma|scrivi\s+[“"]Salva/i.test(text)) return text;
  const title = String(applied.proposal.name || applied.proposal.title || "la proposta");
  switch (applied.proposal.type) {
    case "create_task":
      return `Salvato: ho creato il Task “${title}”.`;
    case "create_meeting":
      return `Salvato: ho creato il meeting “${title}”.`;
    case "create_company":
      return `Salvato: ho creato l’azienda “${title}”.`;
    case "create_deal":
      return `Salvato: ho creato l’opportunità “${title}”.`;
    case "update_task":
      return `Salvato: ho aggiornato il Task “${title}”.`;
    case "update_meeting":
      return `Salvato: ho aggiornato il meeting “${title}”.`;
    case "set_attention_lane":
      return `Applicata: priorità ${String(applied.proposal.lane ?? "").toLowerCase()} per la Stanza “${title}”.`;
    default:
      return `Salvato: ho applicato la proposta “${title}”.`;
  }
}

type Conversation = { id: string; title: string; updatedAt: string; status: string; mode?: string | null; homeTerminalType?: string | null; homeTerminalId?: string | null };
type ResultItem = { type: string; id: string; title: string; subtitle?: string; href?: string; occurredAt?: string; occurredAtLocal?: string; timezone?: string; excerpt?: string; sourceHref?: string; sourceTitle?: string; category?: string; relation?: string; matchedBy?: string; confidence?: number; fileData?: { textStatus?: "available" | "summary_only" | "not_extracted"; evidenceQuality?: "verified" | "quality_unverified" | "review_required" } };

const RESULT_CATEGORY_LABELS: Record<string, string> = {
  crm_people: "Persone nel CRM",
  crm_companies: "Aziende nel CRM",
  crm_activities: "Attività CRM · lead, deal, task, meeting",
  crm_emails: "Email nel CRM",
  crm_files: "File consultati",
  external_people: "Persone · ricerca esterna",
  external_companies: "Aziende · ricerca esterna",
  other_matches: "Corrispondenze varie",
  needs_verification: "Da verificare",
};

const RESULT_TYPE_LABELS: Record<string, string> = {
  company: 'azienda', contact: 'contatto', lead: 'lead', deal: 'opportunità',
  branch: 'filiale', brand: 'brand', synergy: 'sinergia', email: 'email',
  email_message: 'email', meeting: 'appuntamento', task: 'task',
  activity: 'attività', file: 'file', mandante: 'mandante',
  travel_journey: 'viaggio', travel_calendar_event: 'evento collegato',
  travel_calendar_appointment: 'appuntamento collegato',
  travel_reservation: 'prenotazione', travel_expense: 'spesa',
  travel_deal: 'opportunità collegata', travel_expense_summary: 'riepilogo spese',
  travel_integrity: 'verifica necessaria', travel_event_candidate: 'evento proposto',
  travel_appointment_candidate: 'appuntamento proposto',
  travel_reservation_change: 'modifica prenotazione',
  import_session: 'importazione', import_excluded_row: 'riga da verificare',
};

export function resultTypeLabel(type: string): string {
  return RESULT_TYPE_LABELS[type] ?? 'evidenza';
}

export function resultCategoryFor(item: ResultItem): string {
  // Search matches can carry a semantic File category without a content read.
  // Only the explicit verified reader state warrants "File consultati".
  if (item.type === "file" && (item.fileData?.textStatus !== "available"
    || item.fileData.evidenceQuality !== "verified")) return "needs_verification";
  if (item.category && RESULT_CATEGORY_LABELS[item.category]) return item.category;
  // I messaggi storici non hanno sempre il metadato di relazione prodotto dal
  // contratto semantico. Il tipo tecnico da solo non prova che il record sia
  // pertinente alla domanda: mostrarlo come categoria CRM sarebbe una falsa
  // conferma (caso CNCC/aziende nominate solo nel testo di una fonte).
  if (!item.relation && !item.matchedBy) return "needs_verification";
  if (item.type === "contact" || item.type === "lead") return "crm_people";
  if (item.type === "company" || item.type === "branch" || item.type === "brand") return "crm_companies";
  if (item.type === "email") return "crm_emails";
  // A metadata hit is not evidence that Martina read the document.
  if (item.type === "file") return "needs_verification";
  if (["deal", "task", "meeting", "synergy"].includes(item.type)) return "crm_activities";
  return "needs_verification";
}

function resultRelationLabel(relation?: string): string | undefined {
  if (relation === "directly_related") return "relazione diretta";
  if (relation === "explicit_mention") return "menzionato nella fonte";
  if (relation === "correlated") return "correlato";
  if (relation === "candidate_link") return "collegamento candidato";
  return undefined;
}

function orderedResultItems(items: ResultItem[]): ResultItem[] {
  return [...items].sort((left, right) =>
    resultCategoryFor(left).localeCompare(resultCategoryFor(right), "it")
    || left.type.localeCompare(right.type, "it")
    || left.title.localeCompare(right.title, "it"),
  );
}

// Le risposte storiche possono contenere gli evidenziatori PostgreSQL
// prodotti prima della normalizzazione server. Il drawer espone sempre testo
// leggibile, mai markup tecnico; il server resta la fonte per le nuove query.
function readableResultExcerpt(excerpt: string | undefined): string | undefined {
  return excerpt?.replace(/<\/?b>/gi, "") || undefined;
}

export function fileResultPresentation(item: ResultItem): { status: string; preview?: string; note?: string } {
  const raw = readableResultExcerpt(item.excerpt)?.trim();
  const hasText = Boolean(raw && raw !== "Nessun testo estratto disponibile.");
  const textStatus = item.fileData?.textStatus;
  const reviewRequired = item.fileData?.evidenceQuality === "review_required"
    || item.fileData?.evidenceQuality === "quality_unverified"
    || /OCR da verificare|qualità OCR storica non verificata/i.test(item.subtitle || "");
  const status = reviewRequired
    ? "Qualità del testo da verificare"
    : textStatus === "summary_only" ? "Solo sintesi disponibile · testo integrale da verificare"
    : hasText && textStatus === "available" && item.fileData?.evidenceQuality === "verified"
      ? "Testo estratto consultato" : "Contenuto da verificare";
  if (!hasText || textStatus === "not_extracted" || reviewRequired) return { status };
  if (!/\.(xlsx|xls|ods|csv)$/i.test(item.title)) return { status, preview: raw };

  // Il testo tabellare estratto non porta con sé il formato delle celle: un
  // numero Excel a cinque cifre potrebbe essere una data, un importo o un
  // codice. Non lo interpretiamo; anni e quantità ordinarie restano visibili.
  const possibleSerial = /^[2-7]\d{4}(?:[.,]\d+)?$/;
  const numericCellsOmitted = raw!.split(/\t|\r?\n/).some((cell) => possibleSerial.test(cell.trim()));
  const rows = raw!.split(/\r?\n/).map((line) => line.split(/\t+/)
    .map((cell) => cell.trim().replace(/\s+/g, " "))
    .filter((cell) => cell && !possibleSerial.test(cell))
    .join(" · "))
    .filter(Boolean)
    .slice(0, 4);
  return { status, preview: rows.join("\n") || undefined,
    ...(numericCellsOmitted ? { note: "Date o valori a cinque cifre: verifica nel file originale." } : {}) };
}
type MartinaTerminalRelations = {
  contactId?: string | number | null;
  companyId?: string | number | null;
  dealId?: string | number | null;
  leadId?: string | number | null;
  branchId?: string | number | null;
  synergyId?: string | number | null;
  emailMessageId?: string | number | null;
  emailMessageIds?: Array<string | number>;
  emailThreadId?: string | number | null;
  taskId?: string | number | null;
  meetingId?: string | number | null;
  fileIds?: Array<string | number>;
};
type MartinaTerminalOrigin = {
  type: string;
  id?: string;
  label?: string;
  route?: string;
  source?: "record" | "email" | "modal" | "file" | "dashboard" | "calendar" | "global";
  relations?: MartinaTerminalRelations;
};
type ContentBlock = { type: string; text?: string; title?: string; label?: string; requiresExternalConsent?: boolean; sourceDigest?: string; items?: ResultItem[]; failedEntityTypes?: string[]; ambiguity?: { status: "none" | "candidate_set" | "needs_clarification"; subject: string; margin: number; candidates: Array<{ type: string; id: string; title: string; href?: string }> }; options?: Array<{ key: string; label: string; description: string }>; saved?: boolean; requestText?: string; generatedAt?: string; crmQuery?: MartinaCrmQueryView; proposal?: any; origin?: MartinaTerminalOrigin; status?: string; entity?: { type: string; id: number }; lastError?: { code?: string; message: string; at?: string }; query?: string; searchCount?: number; mode?: "compose" | "reply" | "reply_all" | "forward"; language?: string; sourceMessageId?: number; threadId?: number | null; subject?: string | null; sentAt?: string; delivery?: CanonicalEmailSendResult['delivery']; sentMessageId?: number; recipientContactId?: number; recipientLabel?: string; to?: string; accountId?: number; source?: string; sources?: unknown[] };
type Message = { id: string; role: "user" | "assistant" | "system" | "tool"; plainText: string; contentBlocks?: ContentBlock[]; status: string; createdAt: string; clientRequestId?: string | null; terminalContext?: MartinaTerminalContext };

export function EmailSendOutcome({ delivery, sentAt }: {
  delivery?: CanonicalEmailSendResult['delivery'];
  sentAt?: string;
}) {
  const feedback = canonicalEmailSendFeedback({ delivery });
  const partial = feedback.variant === 'destructive';
  return <div role={partial ? 'alert' : 'status'} className={`w-full min-w-0 border px-2 py-2 ${partial ? 'border-destructive text-destructive' : 'border-border text-foreground'}`}>
    <div className="flex items-center gap-1 text-xs font-semibold">
      {partial ? <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> : <Send className="h-3.5 w-3.5 shrink-0" />}
      <span>{feedback.title}</span>
    </div>
    <p className="mt-1 break-words text-xs font-normal">{feedback.description}</p>
    {sentAt ? <p className="mt-1 text-[11px] font-normal text-muted-foreground">Registrato il {new Date(sentAt).toLocaleString('it-IT')}</p> : null}
  </div>;
}
export function MartinaMessageSources({ contentBlocks }: { contentBlocks?: unknown }) {
  const sources = memorySourcesForMessage({ contentBlocks });
  if (!sources.length) return null;
  return <section className="mt-3 border-t border-border pt-3" aria-label="Fonti di questa risposta">
    <p className="text-xs font-medium">Fonti memoria</p>
    {sources.map(({ key, href, label }) => href
      ? <a className="block break-words text-xs text-primary underline" key={key} href={href}>{label}</a>
      : <span className="block break-words text-xs text-muted-foreground" key={key}>{label} · link non disponibile</span>)}
  </section>;
}
const STORAGE_KEY = "exai-active-conversation-id";
const ACTIVE_RUN_STORAGE_KEY = "exai-active-persistent-run";
export const MARTINA_RUN_POLL_INTERVAL_MS = 1_000;
export const MARTINA_RUN_MAX_DELIVERY_FAILURES = 60;
type PersistedMartinaRun = { requestId: string; runId: string; conversationId: string };
export const MARTINA_TURN_CONTEXT_VERSION = "martina-turn-context-v1" as const;

export type MartinaTurnSurface =
  | "chat"
  | "feed"
  | "room_drawer"
  | "dashboard"
  | "entity"
  | "email"
  | "calendar"
  | "composer"
  | "file";

export type MartinaTurnRef = {
  type: string;
  id: string;
  label?: string;
};

export type MartinaTerminalContext = {
  version?: typeof MARTINA_TURN_CONTEXT_VERSION;
  surface?: MartinaTurnSurface;
  terminalType?: "global" | "dashboard" | "entity" | "email" | "calendar" | "file";
  entityType?: string;
  entityId?: string;
  route?: string;
  title?: string;
  selectedFileIds?: number[];
  threadId?: number | null;
  composeMode?: "compose" | "reply" | "forward";
  composeSubject?: string;
  composeTo?: string[];
  composeBody?: string;
  parentEntityType?: string;
  parentEntityId?: string;
  parentTitle?: string;
  activationKind?: "prepare_email_draft";
  composerAssist?: boolean;
  relations?: MartinaTerminalRelations;
  origin?: MartinaTerminalOrigin;
  primary?: MartinaTurnRef;
  selected?: MartinaTurnRef;
  parent?: MartinaTurnRef;
  roomId?: string | number;
  timezone?: string;
  conversationId?: string;
};

export function martinaClientTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

export function toMartinaTurnContextPayload(
  context: MartinaTerminalContext | undefined,
  conversationId?: string,
): MartinaTerminalContext {
  const timezone = context?.timezone || martinaClientTimezone();
  return {
    version: MARTINA_TURN_CONTEXT_VERSION,
    ...context,
    route: context?.route ?? (typeof window !== "undefined" ? window.location.pathname : undefined),
    ...(conversationId ? { conversationId } : {}),
    ...(timezone ? { timezone } : {}),
  };
}

/**
 * Caso reale Rinaldi (2 ago 2026): la bozza nata dal composer veniva spedita
 * dal drawer come SECONDA email costruita dal solo blocco (destinatario,
 * oggetto, testo). L'allegato, i CC/BCC, il deal e la firma restavano nel
 * composer insieme all'email vera, mai partita, mentre Martina confermava
 * "email inviata". Quando la bozza appartiene al composer, l'invio deve
 * tornare al composer: e' l'unico che possiede l'email completa.
 */
export function draftBelongsToComposer(message: Pick<Message, "terminalContext">) {
  return message.terminalContext?.composerAssist === true;
}

function conversationScope(context?: MartinaTerminalContext) {
  const bound = Boolean(
    context?.terminalType && context.terminalType !== "global" &&
    context.entityType && context.entityId,
  );
  return {
    bound,
    homeTerminalType: bound ? context!.entityType! : null,
    homeTerminalId: bound ? String(context!.entityId) : null,
    mode: bound ? (context!.terminalType === "email" ? "email" : "entity") : "global",
  } as const;
}

function conversationMatchesScope(conversation: Conversation, scope: ReturnType<typeof conversationScope>) {
  if (scope.bound) {
    return conversation.homeTerminalType === scope.homeTerminalType && conversation.homeTerminalId === scope.homeTerminalId;
  }
  return !conversation.homeTerminalType && !conversation.homeTerminalId && (conversation.mode ?? "global") === "global";
}

function contextFromConversation(conversation: Conversation): MartinaTerminalContext | undefined {
  if (!conversation.homeTerminalType || !conversation.homeTerminalId) return undefined;
  const terminalType = conversation.mode === "email"
    ? "email" as const
    : conversation.homeTerminalType === "file"
      ? "file" as const
      : "entity" as const;
  const route = conversation.homeTerminalType === "martina_room"
    ? `/?room=${conversation.homeTerminalId}&drawer=1`
    : conversation.homeTerminalType === "email_message"
      ? `/emails?emailId=${conversation.homeTerminalId}`
      : conversation.homeTerminalType === "email_thread"
        ? `/emails?threadId=${conversation.homeTerminalId}`
        : conversation.homeTerminalType === "file"
          ? `/files?fileId=${conversation.homeTerminalId}`
        : "/";
  return {
    terminalType,
    entityType: conversation.homeTerminalType,
    entityId: conversation.homeTerminalId,
    route,
    title: conversation.title,
  };
}
function relationEntries(context?: MartinaTerminalContext): Array<{ label: string; value: string; href?: string }> {
  const relations = context?.relations;
  if (!relations) return [];
  const entries: Array<{ label: string; value: string; href?: string }> = [];
  const refLabel = (type: string, value: string | number) => {
    const id = String(value);
    const refs = [context?.primary, context?.selected, context?.parent];
    const direct = refs.find((ref) => ref?.type === type && ref.id === id)?.label;
    if (direct?.trim()) return direct.trim();
    if (context?.entityType === type && context.entityId === id && context.title?.trim()) return context.title.trim();
    if (context?.origin?.type === type && context.origin.id === id && context.origin.label?.trim()) return context.origin.label.trim();
    return null;
  };
  const add = (label: string, type: string, value: string | number | null | undefined, hrefPrefix?: string) => {
    if (value === null || value === undefined || value === "") return;
    const humanLabel = refLabel(type, value);
    if (!humanLabel) return;
    const href = hrefPrefix ? `${hrefPrefix}${encodeURIComponent(String(value))}` : undefined;
    // Solo i link al lettore email portano returnTo, per tornare qui alla chiusura.
    entries.push({ label, value: humanLabel, href: href?.startsWith("/emails") ? withReturnTo(href) : href });
  };
  add("Contatto", "contact", relations.contactId, "/contacts/");
  add("Azienda", "company", relations.companyId, "/companies/");
  add("Opportunità", "deal", relations.dealId, "/deals/");
  add("Lead", "lead", relations.leadId, "/leads/");
  add("Filiale", "branch", relations.branchId, "/branches/");
  add("Email", "email_message", relations.emailMessageId, "/emails?emailId=");
  add("Conversazione", "email_thread", relations.emailThreadId);
  add("Attività", "task", relations.taskId, "/tasks?taskId=");
  add("Appuntamento", "meeting", relations.meetingId, "/meetings/");
  return entries.slice(0, 6);
}

function ActiveContextBar({ terminalContext, onUseGlobalContext }: { terminalContext?: MartinaTerminalContext; onUseGlobalContext?: () => void }) {
  const hasContext = Boolean(terminalContext?.terminalType && terminalContext.terminalType !== "global");
  // Il contesto globale è già descritto nell'header: una seconda barra ripete
  // la stessa informazione e sottrae spazio alla conversazione.
  if (!hasContext) return null;
  const title = terminalContext?.title || terminalContext?.entityType || "Contesto corrente";
  const contextHref = terminalContext?.route;
  const relations = relationEntries(terminalContext);
  return (
    <div className="shrink-0 border-b border-border bg-muted/30 px-3 py-2" data-testid="martina-active-context">
      <div className="flex items-center gap-2">
        <div className="flex min-w-0 flex-1 items-baseline gap-2">
          <span className="shrink-0 text-[11px] font-medium text-muted-foreground">Contesto attivo</span>
          <span className="truncate text-xs font-medium text-foreground" title={title}>{title}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {contextHref && (
            <a href={contextHref} className="inline-flex h-8 items-center gap-1 border border-border bg-background px-2 text-xs font-medium hover:bg-muted">
              <ExternalLink className="h-3.5 w-3.5" />
              Apri fonte
            </a>
          )}
          {onUseGlobalContext && (
            <Button type="button" size="sm" variant="outline" className="h-8 rounded-none gap-1 px-2 text-xs" onClick={onUseGlobalContext}>
              <Unlink className="h-3.5 w-3.5" />
              Sgancia
            </Button>
          )}
        </div>
      </div>
      {relations.length > 0 && (
        <div className="mt-1.5 flex gap-1 overflow-x-auto pb-1">
          {relations.map((relation) => relation.href ? (
            <a key={`${relation.label}:${relation.value}`} href={relation.href} className="shrink-0 border border-border bg-background px-2 py-1 text-[11px] text-foreground hover:bg-muted">
              {relation.label}: {relation.value}
            </a>
          ) : (
            <span key={`${relation.label}:${relation.value}`} className="shrink-0 border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground">
              {relation.label}: {relation.value}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function toLocalDateTime(value?: string) {
  if (!value) return "";
  const date = new Date(value); const pad = (number: number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function MartinaGlobalDrawer({ externalOpen, onExternalOpenChange, terminalContext, onUseGlobalContext, initialPrompt, autoSubmitPrompt, autoStartVoice, preferredConversationId, onInsertEmailReply, onSendFromComposer, getComposerSendReview }: { externalOpen?: boolean; onExternalOpenChange?: (open: boolean) => void; terminalContext?: MartinaTerminalContext; onUseGlobalContext?: () => void; initialPrompt?: string; autoSubmitPrompt?: boolean; autoStartVoice?: boolean; preferredConversationId?: string; onInsertEmailReply?: (draft: { body: string; mode: "compose" | "reply" | "reply_all" | "forward"; language?: string }) => void; onSendFromComposer?: MartinaEmailComposerApprovedSend; getComposerSendReview?: MartinaEmailComposerReviewReader }) {
  const { data: operatorNotificationCount = 0 } = useMartinaOperatorNotificationCount();
  const [location, navigate] = useLocation();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen ?? internalOpen;
  const setOpen = onExternalOpenChange ?? setInternalOpen;
  // Non inizializzare dal localStorage: un id globale salvato dal browser non
  // puo' diventare la cronologia di un'email o di un altro record.
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [showConversationHistory, setShowConversationHistory] = useState(false);
  const [startNewGlobalTopic, setStartNewGlobalTopic] = useState(false);
  const [selectedGlobalTopicId, setSelectedGlobalTopicId] = useState<string | null>(null);
  // F-005: auto-submit Feed attende la fine dell'hydrate open (anche se
  // conversationId resta null per scope vincolato vuoto).
  const [conversationScopeReady, setConversationScopeReady] = useState(false);
  // Un turn concluso dopo che l'utente ha cambiato fonte non deve mai
  // ridisegnare la conversazione precedente nel nuovo contesto.
  const activeConversationIdRef = useRef<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [preferredConversationContext, setPreferredConversationContext] = useState<MartinaTerminalContext | undefined>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [segments, setSegments] = useState<MartinaComposerSegment[]>(() => composerSegmentsFromPlainText(""));
  const [mentionChoices, setMentionChoices] = useState<MartinaComposerCrmHit[]>([]);
  const [composerCaret, setComposerCaret] = useState(0);
  const mentionGateRef = useRef(0);
  const [interim, setInterim] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const [runPhase, setRunPhase] = useState<"queued" | "crm_deterministic" | "provider_synthesis" | "finalizing">("queued");
  const [error, setError] = useState<string | null>(null);
  const [retryableError, setRetryableError] = useState(true);
  const [editingProposalKey, setEditingProposalKey] = useState<string | null>(null);
  const [editingEmailDraftKey, setEditingEmailDraftKey] = useState<string | null>(null);
  const [emailDraftEdit, setEmailDraftEdit] = useState<{ text: string; subject: string }>({ text: "", subject: "" });
  const [sendingDraftKey, setSendingDraftKey] = useState<string | null>(null);
  const [savingDraftKey, setSavingDraftKey] = useState<string | null>(null);
  const [pendingEmailSend, setPendingEmailSend] = useState<{
    message: Message; index: number; block: ContentBlock; composer: boolean; review?: MartinaEmailComposerReview;
  } | null>(null);
  const [reviewingComposer, setReviewingComposer] = useState(false);
  const reviewRequestRef = useRef(0);
  useEffect(() => { if (!open) reviewRequestRef.current += 1; }, [open]);
  const reviewComposerDraft = async (message: Message, index: number, block: ContentBlock) => {
    const request = ++reviewRequestRef.current;
    setReviewingComposer(true);
    setError(null);
    try {
      const review = await getComposerSendReview?.(block.text || '');
      if (request !== reviewRequestRef.current) return;
      if (!review) { setError('Salva la bozza e riapri il compositore prima di confermare l’invio.'); return; }
      setPendingEmailSend({ message, index, block, composer: true, review: structuredClone(review) });
    } catch { setError('Non è stato possibile preparare la conferma. Nessuna email è stata inviata.'); }
    finally { setReviewingComposer(false); }
  };
  const [proposalDraft, setProposalDraft] = useState<any>(null);
  const activeTerminalContext = terminalContext ?? preferredConversationContext;
  const [dismissedResultKeys, setDismissedResultKeys] = useState<Set<string>>(new Set());
  const lastSent = useRef<{ text: string; assistMode?: "provider"; sourceAssistantMessageId?: string; displayText?: string; requestIdOverride?: string } | null>(null);
  /** F-004: last minted requestId before sessionStorage pointer write. */
  const pendingRequestIdRef = useRef<{ conversationId: string; requestId: string } | null>(null);
  const messageEndRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const voiceAutoStarted = useRef(false);
  const ignoreVoiceTranscriptRef = useRef(false);
  const voiceSendGenerationRef = useRef(0);
  const drawerOpenRef = useRef(open);
  const autoSubmittedPromptKey = useRef<string | null>(null);
  const autoSubmitInFlightKey = useRef<string | null>(null);
  const autoSendVoiceRef = useRef<(transcript: string) => void>(() => undefined);
  const activeRequestRef = useRef<{ requestId: string; runId?: string; controller: AbortController } | null>(null);
  useEffect(() => { setShowConversationHistory(false); }, [conversationId]);
  const { isSupported, isListening, error: voiceError, start, stop, cancel } = useVoiceRecognition((finalText, interimText) => {
    if (ignoreVoiceTranscriptRef.current) return;
    setSegments(composerSegmentsFromPlainText(finalText)); setInterim(interimText);
  }, (transcript) => { if (!ignoreVoiceTranscriptRef.current) autoSendVoiceRef.current(transcript); }, 4_500, { focusTargetRef: composerRef });
  const cancelVoiceCapture = useCallback(() => {
    voiceSendGenerationRef.current++;
    ignoreVoiceTranscriptRef.current = true;
    cancel();
    setInterim("");
  }, [cancel]);
  const startVoiceCapture = () => {
    voiceSendGenerationRef.current++;
    ignoreVoiceTranscriptRef.current = false;
    start();
  };
  const voiceScope = `${location}|${terminalContext?.terminalType ?? ""}|${terminalContext?.entityType ?? ""}|${terminalContext?.entityId ?? ""}|${preferredConversationId ?? ""}`;
  const voiceScopeRef = useRef(voiceScope);
  voiceScopeRef.current = voiceScope;
  const previousVoiceScopeRef = useRef(voiceScope);
  useLayoutEffect(() => {
    drawerOpenRef.current = open;
    if (!open) cancelVoiceCapture();
  }, [open, cancelVoiceCapture]);
  useLayoutEffect(() => {
    if (previousVoiceScopeRef.current !== voiceScope) {
      previousVoiceScopeRef.current = voiceScope;
      cancelVoiceCapture();
    }
  }, [voiceScope, cancelVoiceCapture]);
  useEffect(() => {
    if (!open || isListening || loading) return;
    // Invalida subito le risposte precedenti: passando da `@Al` a `@Ines`
    // l'elenco vecchio non deve restare visibile durante il debounce.
    const gate = mentionGateRef.current + 1;
    mentionGateRef.current = gate;
    setMentionChoices([]);
    const display = displayComposerText(segments);
    const caret = Math.max(0, Math.min(composerCaret, display.length));
    const at = activeComposerAtQuery(segments, caret);
    const plainName = at ? null : activeComposerPlainNameQuery(segments, caret);
    const query = at && at.query.trim().length >= 2
      ? at.query.trim()
      : plainName?.text ?? null;
    if (!query || query.length < 2) {
      return;
    }
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const hits = await fetchComposerMentionHits(query);
          const uniqueness = resolveComposerMentionUniqueness(query, hits);
          if (gate !== mentionGateRef.current) return;
          // Anche un match unico deve essere confermato nel picker: il testo
          // digitato non è ancora un riferimento CRM finché l'utente non lo
          // seleziona esplicitamente.
          setMentionChoices(uniqueness.status === "ambiguous" ? uniqueness.hits : hits.slice(0, 12));
        } catch {
          if (gate === mentionGateRef.current) setMentionChoices([]);
        }
      })();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [open, isListening, loading, segments, composerCaret]);
  const loadConversations = async () => {
    const data = await apiRequest("/api/exai/conversations?limit=100");
    setConversations(data.conversations ?? []);
    return data.conversations as Conversation[];
  };
  const loadMessages = async (id: string) => {
    const data = await apiRequest(`/api/exai/conversations/${id}/messages`);
    if (activeConversationIdRef.current === id) setMessages(data.messages ?? []);
    return data.messages ?? [];
  };
  useEffect(() => {
    if (!open) {
      setConversationScopeReady(false);
      return;
    }
    if (initialPrompt) {
      setSegments(composerSegmentsFromPlainText(initialPrompt));
      setMentionChoices([]);
    }
    setError(null);
    setConversationScopeReady(false);
    const scope = conversationScope(terminalContext);
    let cancelled = false;
    activeConversationIdRef.current = null;
    setConversationId(null);
    setPreferredConversationContext(undefined);
    setMessages([]);
    setEditingProposalKey(null);
    setEditingEmailDraftKey(null);
    setEmailDraftEdit({ text: "", subject: "" });
    setProposalDraft(null);
    loadConversations().then(async (items) => {
      if (cancelled) return;
      try {
        const preferredItem = preferredConversationId
          ? items.find((item) => item.id === preferredConversationId)
            ?? await apiRequest(`/api/exai/conversations/${preferredConversationId}`).catch(() => null)
          : null;
        if (cancelled) return;
        const preferred = preferredItem && (!terminalContext || conversationMatchesScope(preferredItem, scope)) ? preferredConversationId : null;
        if (preferredItem && !terminalContext) setPreferredConversationContext(contextFromConversation(preferredItem));
        const selectedTopic = selectedGlobalTopicId && items.some((item) => item.id === selectedGlobalTopicId && conversationMatchesScope(item, scope))
          ? selectedGlobalTopicId : null;
        const scoped = items.find((item) => conversationMatchesScope(item, scope))?.id ?? null;
        // Un contesto vincolato parte vuoto se non ha ancora una conversazione:
        // creare/riusare la conversazione globale qui riaprirebbe il bug Mannara.
        const selected = preferred ?? (!scope.bound && startNewGlobalTopic ? null : selectedTopic ?? scoped);
        activeConversationIdRef.current = selected;
        setConversationId(selected);
        if (selected) {
          localStorage.setItem(STORAGE_KEY, selected);
          const data = await apiRequest(`/api/exai/conversations/${selected}/messages`);
          if (!cancelled && activeConversationIdRef.current === selected) setMessages(data.messages ?? []);
        } else if (!scope.bound) {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch {
        if (!cancelled) setError("Impossibile caricare le conversazioni di Martina.");
      } finally {
        if (!cancelled) setConversationScopeReady(true);
      }
    }).catch(() => {
      if (cancelled) return;
      setError("Impossibile caricare le conversazioni di Martina.");
      // Fail-open: consente ensureConversation sul send manuale / auto-submit.
      setConversationScopeReady(true);
    });
    return () => {
      cancelled = true;
      // Un cambio di contesto interrompe solo la consegna nel drawer: il run
      // persistente resta riconnettibile tramite sessionStorage. Senza questo
      // abort, una richiesta della conversazione precedente può completare
      // sulla nuova superficie e lasciare spinner/errori nello scope sbagliato.
      const active = activeRequestRef.current;
      if (active) {
        active.controller.abort(new Error("MARTINA_DRAWER_SCOPE_CHANGED"));
        activeRequestRef.current = null;
        setLoading(false);
      }
    };
  }, [open, terminalContext?.terminalType, terminalContext?.entityType, terminalContext?.entityId, preferredConversationId]);
  // F-010: openMartina può aggiornare initialPrompt a drawer già aperto
  // (secondo messaggio Feed); l'open-effect non ha initialPrompt nelle deps.
  useEffect(() => {
    if (!open || !initialPrompt?.trim()) return;
    setSegments(composerSegmentsFromPlainText(initialPrompt));
    setMentionChoices([]);
  }, [open, initialPrompt]);
  useEffect(() => {
    if (!open) { voiceAutoStarted.current = false; return; }
    if (autoStartVoice && isSupported && !voiceAutoStarted.current) {
      voiceAutoStarted.current = true; startVoiceCapture();
    }
  }, [open, autoStartVoice, isSupported, start]);
  useEffect(() => {
    if (!loading) { setLoadingSeconds(0); return; }
    const startedAt = Date.now();
    const timer = window.setInterval(() => setLoadingSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [loading]);
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ block: "end", behavior: loading ? "auto" : "smooth" });
  }, [messages, loading]);

  const ensureConversation = async (titleHint?: string) => {
    if (conversationId) return conversationId;
    const scope = conversationScope(activeTerminalContext);
    const created = await apiRequest("/api/exai/conversations", { method: "POST", notifySuccess: false, data: {
      title: scope.bound && activeTerminalContext?.title ? `Martina · ${activeTerminalContext.title}` : startNewGlobalTopic && titleHint?.trim() ? titleHint.trim().slice(0, 100) : "Conversazione con Martina",
      mode: scope.mode,
      homeTerminalType: scope.homeTerminalType,
      homeTerminalId: scope.homeTerminalId,
      startNewTopic: !scope.bound && startNewGlobalTopic,
    } });
    activeConversationIdRef.current = created.id;
    setConversationId(created.id);
    if (!scope.bound) { setStartNewGlobalTopic(false); setSelectedGlobalTopicId(created.id); }
    localStorage.setItem(STORAGE_KEY, created.id); await loadConversations(); return created.id as string;
  };
  const beginNewGlobalTopic = () => {
    if (loading) return;
    cancelVoiceCapture();
    activeConversationIdRef.current = null;
    setConversationId(null);
    setMessages([]);
    setShowConversationHistory(false);
    setSelectedGlobalTopicId(null);
    setStartNewGlobalTopic(true);
    lastSent.current = null;
    pendingRequestIdRef.current = null;
  };
  const selectGlobalTopic = async (id: string) => {
    if (loading || !id) return;
    cancelVoiceCapture();
    activeConversationIdRef.current = id;
    setConversationId(id);
    setMessages([]);
    setShowConversationHistory(false);
    setSelectedGlobalTopicId(id);
    setStartNewGlobalTopic(false);
    await loadMessages(id).catch(() => setError("Impossibile caricare questo argomento."));
  };
  const sendEmailDraft = async (message: Message, blockIndex: number, type: "email_reply_draft" | "email_compose_draft") => {
    if (loading) return;
    const key = `${message.id}:${blockIndex}`;
    setLoading(true); setSendingDraftKey(key); setError(null);
    try {
      const route = type === "email_compose_draft" ? "email-compose-drafts" : "email-reply-drafts";
      await apiRequest(`/api/exai/assistant/${route}/${message.id}/${blockIndex}/send`, {
        method: "POST",
        // Contratto di sicurezza: solo il click sul pulsante della bozza
        // visibile può dichiarare l'approvazione dell'utente al server.
        headers: { "X-Martina-Email-Approval": "explicit-ui-v1" },
        // Il server confronta il contesto SALVATO sulla bozza con quello
        // dichiarato dal client: va quindi inviato il contesto del messaggio
        // che contiene la bozza, non quello attivo del drawer (che può essere
        // cambiato dopo la generazione → falso 409 EMAIL_DRAFT_CONTEXT_CHANGED).
        data: {
          terminalContext: message.terminalContext ?? activeTerminalContext ?? { terminalType: "global" },
          approvedDraft: martinaEmailDraftApproval(message.contentBlocks?.[blockIndex]),
        },
      });
      const id = conversationId ?? await ensureConversation();
      await loadMessages(id); await loadConversations();
    } catch (reason) {
      setError(martinaUiErrorMessage(reason, "Invio email non riuscito. La bozza resta disponibile: ricontrollala prima di riprovare."));
      if (conversationId) void loadMessages(conversationId).catch(() => undefined);
    } finally {
      setSendingDraftKey(null); setLoading(false);
    }
  };

  const saveEmailDraft = async (message: Message, blockIndex: number) => {
    const key = `${message.id}:${blockIndex}`;
    if (!emailDraftEdit.text.trim() || savingDraftKey) return;
    setSavingDraftKey(key); setError(null);
    try {
      await apiRequest(`/api/exai/assistant/email-drafts/${message.id}/${blockIndex}`, {
        method: "PATCH",
        notifySuccess: false,
        data: { text: emailDraftEdit.text, ...(emailDraftEdit.subject.trim() ? { subject: emailDraftEdit.subject } : {}) },
      });
      const id = conversationId ?? await ensureConversation();
      await loadMessages(id);
      setEditingEmailDraftKey(null);
    } catch (error) {
      setError(martinaUiErrorMessage(error, "La bozza non è stata modificata."));
    } finally {
      setSavingDraftKey(null);
    }
  };

  const dismissEmailDraft = async (message: Message, blockIndex: number) => {
    const key = `${message.id}:${blockIndex}`;
    if (savingDraftKey) return;
    setSavingDraftKey(key); setError(null);
    try {
      await apiRequest(`/api/exai/assistant/email-drafts/${message.id}/${blockIndex}/dismiss`, { method: "POST", notifySuccess: false });
      const id = conversationId ?? await ensureConversation();
      await loadMessages(id);
      setEditingEmailDraftKey(null);
    } catch (error) {
      setError(martinaUiErrorMessage(error, "La bozza è rimasta invariata."));
    } finally {
      setSavingDraftKey(null);
    }
  };

  const waitForPersistentRun = async (runId: string, controller: AbortController, initialSequence = 0, deadlineAt?: number) => {
    let afterSequence = initialSequence;
    let deliveryFailures = 0;
    let lastKnownStatus: string | undefined;
    const observeUntil = resolveMartinaRunObserveUntil(deadlineAt);
    while (!controller.signal.aborted) {
      if (Date.now() >= observeUntil) {
        // Budget scaduto: un solo GET finale per leggere un esito già persistito.
        try {
          const run = await apiRequest(`/api/exai/assistant/runs/${runId}`, { signal: controller.signal, notifySuccess: false });
          deliveryFailures = 0;
          if (run.phase) setRunPhase(run.phase);
          if (isMartinaRunTerminalStatus(run.status)) return run;
        } catch (error) {
          if (controller.signal.aborted) throw error;
          if (shouldInvalidatePersistentRun(error)) throw error;
        }
        throw new Error(MARTINA_RUN_OBSERVE_BUDGET_EXCEEDED);
      }
      try {
        // F-021: una sola GET/s (events). Lo status è sullo stesso payload evento;
        // GET run solo a chiusura terminale o allo scadere di observeUntil.
        const eventPage = await apiRequest(`/api/exai/assistant/runs/${runId}/events?afterSequence=${afterSequence}`, { signal: controller.signal, notifySuccess: false });
        deliveryFailures = 0;
        for (const event of eventPage.events ?? []) {
          if (event.phase) setRunPhase(event.phase);
          if (typeof event.state === "string" && event.state) lastKnownStatus = event.state;
          afterSequence = Math.max(afterSequence, Number(event.sequence) || 0);
        }
        // State can become terminal through lease recovery without a final event.
        // Use the same owner-scoped response, without an additional poll.
        if (eventPage.phase) setRunPhase(eventPage.phase);
        if (typeof eventPage.status === "string") lastKnownStatus = eventPage.status;
        if (isMartinaRunTerminalStatus(lastKnownStatus)) {
          const run = await apiRequest(`/api/exai/assistant/runs/${runId}`, { signal: controller.signal, notifySuccess: false });
          if (run.phase) setRunPhase(run.phase);
          if (isMartinaRunTerminalStatus(run.status)) return run;
          lastKnownStatus = typeof run.status === "string" ? run.status : lastKnownStatus;
        }
      } catch (error) {
        if (controller.signal.aborted) throw error;
        // A persisted pointer to a run that no longer exists is terminal for
        // the client. Retrying it only freezes the drawer and blocks the next
        // user turn; transient network failures still use the bounded retry.
        if (shouldInvalidatePersistentRun(error)) throw error;
        deliveryFailures += 1;
        if (deliveryFailures >= MARTINA_RUN_MAX_DELIVERY_FAILURES) {
          throw new Error(MARTINA_RUN_DELIVERY_RECONNECT_REQUIRED);
        }
        setError("Connessione temporaneamente assente: il run Martina continua e la riconnessione è in corso.");
      }
      await new Promise<void>((resolve, reject) => {
        let timer: number;
        const onAbort = () => { window.clearTimeout(timer); controller.signal.removeEventListener("abort", onAbort); reject(controller.signal.reason); };
        timer = window.setTimeout(() => {
          controller.signal.removeEventListener("abort", onAbort);
          resolve();
        }, MARTINA_RUN_POLL_INTERVAL_MS);
        controller.signal.addEventListener("abort", onAbort, { once: true });
      });
    }
    throw controller.signal.reason ?? new Error("MARTINA_RUN_DELIVERY_ABORTED");
  };

  const applyPersistentRunResult = async (id: string, result: any) => {
    if (activeConversationIdRef.current !== id) return;
    const completed = result.result ?? {};
    const answerText = completed.answer?.find((item: any) => item?.type === "text")?.text;
    const envelopeFailed = completed.status === "failed" || result.status === "failed";
    if (completed.assistantMessageId && answerText) {
      setMessages((current) => current.some((message) => message.id === completed.assistantMessageId)
        ? current
        : [...current, {
            id: completed.assistantMessageId,
            role: "assistant",
            plainText: answerText,
            contentBlocks: completed.contentBlocks ?? [],
            status: envelopeFailed ? "failed" : (completed.status ?? result.status ?? "completed"),
            createdAt: new Date().toISOString(),
          }]);
    }
    if (envelopeFailed) {
      setError(completed.message ?? result.errorMessage ?? "Martina non ha completato il run. Il motivo è stato registrato.");
      setRetryableError(result.errorCode !== "MARTINA_RUN_CANCELLED" && result.retryable !== false && completed.retryable !== false);
    }
    void Promise.all([loadMessages(id), loadConversations()]).catch(() => undefined);
  };

  useEffect(() => {
    if (!open || !conversationId || loading) return;
    let persisted: PersistedMartinaRun | null = null;
    try {
      const raw = sessionStorage.getItem(ACTIVE_RUN_STORAGE_KEY);
      persisted = raw ? JSON.parse(raw) as PersistedMartinaRun : null;
    } catch {
      sessionStorage.removeItem(ACTIVE_RUN_STORAGE_KEY);
    }
    if (!persisted || persisted.conversationId !== conversationId) return;

    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError("Riconnessione al run Martina in corso…");
    setRunPhase("queued");
    activeRequestRef.current = { requestId: persisted.requestId, runId: persisted.runId, controller };
    void waitForPersistentRun(persisted.runId, controller)
      .then(async (result) => {
        if (cancelled) return;
        await applyPersistentRunResult(conversationId, result);
        sessionStorage.removeItem(ACTIVE_RUN_STORAGE_KEY);
      })
      .catch((reason) => {
        if (cancelled || controller.signal.aborted) return;
        if (shouldClearPersistentRunPointer(reason)) {
          // Do not let a missing/expired run pointer trap every subsequent
          // drawer open in reconnect mode. The server run remains untouched.
          sessionStorage.removeItem(ACTIVE_RUN_STORAGE_KEY);
          setError(persistentRunRecoveryMessage(reason));
          void loadMessages(conversationId).catch(() => undefined);
        } else {
          setError(martinaUiErrorMessage(reason, "La consegna è ancora in attesa di riconnessione; il run non è stato cancellato."));
        }
        setRetryableError(true);
      })
      .finally(() => {
        if (activeRequestRef.current?.requestId === persisted?.requestId) activeRequestRef.current = null;
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (activeRequestRef.current?.requestId === persisted?.requestId) {
        activeRequestRef.current = null;
      }
      // FE-01: il finally sotto salta setLoading se cancelled. Il cleanup deve
      // sbloccare sempre Invia/Stop, anche chiudendo il drawer o cambiando
      // conversazione a metà riconnessione.
      setLoading(false);
      controller.abort(new Error("MARTINA_DRAWER_DELIVERY_PAUSED"));
    };
  }, [open, conversationId]);

  const send = async (retry?: { text: string; append?: boolean; assistMode?: "provider"; sourceAssistantMessageId?: string; displayText?: string; requestIdOverride?: string }): Promise<boolean> => {
    // Un picker aperto rappresenta sempre una scelta CRM ancora da compiere,
    // sia dopo `@` sia dopo un nome completo scritto normalmente. Finché è
    // visibile, Invia non può spedire testo non ancora collegato.
    const mentionSelectionPending = Boolean(mentionChoices.length);
    if (!retry && mentionSelectionPending) return false;
    const content = (retry?.text ?? serializeComposerPayload(segments)).trim();
    const visible = retry?.displayText ?? (retry?.text ? stripComposerTransportForDisplay(retry.text) : displayComposerText(segments).trim());
    if (!conversationScopeReady || !content || loading) return false;
    if (isListening) stop(); setLoading(true); setError(null); setRetryableError(true); setRunPhase("queued");
    lastSent.current = {
      text: content,
      ...(retry?.assistMode ? { assistMode: retry.assistMode } : {}),
      ...(retry?.sourceAssistantMessageId ? { sourceAssistantMessageId: retry.sourceAssistantMessageId } : {}),
      ...(retry?.displayText ? { displayText: retry.displayText } : {}),
      ...(retry?.requestIdOverride ? { requestIdOverride: retry.requestIdOverride } : {}),
    };
    const controller = new AbortController();
    let sentConversationId: string | null = null;
    let requestId = "";
    let optimisticUserId: string | null = null;
    let composerClearedAfterPersist = false;
    try {
      const id = await ensureConversation(visible || content);
      sentConversationId = id;
      if (activeConversationIdRef.current !== id) {
        setLoading(false);
        setError("Il contesto della conversazione è cambiato prima dell'invio. Il testo è ancora nel composer: riprova.");
        return false;
      }
      let persisted: PersistedMartinaRun | null = null;
      try {
        const raw = sessionStorage.getItem(ACTIVE_RUN_STORAGE_KEY);
        persisted = raw ? JSON.parse(raw) as PersistedMartinaRun : null;
      } catch {
        sessionStorage.removeItem(ACTIVE_RUN_STORAGE_KEY);
      }
      const plan = planMartinaTurnSend({
        conversationId: id,
        mintRequestId: () => retry?.requestIdOverride ?? crypto.randomUUID(),
        persisted,
        pendingRequestId: pendingRequestIdRef.current?.conversationId === id
          ? pendingRequestIdRef.current.requestId
          : null,
      });
      requestId = plan.requestId;
      if (plan.mode === "enqueue") {
        pendingRequestIdRef.current = { conversationId: id, requestId };
      }
      activeRequestRef.current = { requestId, runId: plan.mode === "reconnect" ? plan.runId : undefined, controller };
      if (!retry || retry.append) {
        if (plan.mode === "enqueue") {
          optimisticUserId = requestId;
          setMessages((current) => [...current, { id: requestId, role: "user", plainText: visible || content, status: "completed", createdAt: new Date().toISOString() }]);
        }
      }
      // Non svuotare prima del persist: se il POST fallisce in ingresso, Michele
      // deve ritrovare il testo e poterlo reinviare senza riscrivere.
      const deadlineAt = interactiveTurnDeadlineAt();
      let accepted: any;
      if (plan.mode === "reconnect") {
        accepted = { runId: plan.runId, requestId: plan.requestId };
      } else {
        try {
          accepted = await apiRequest("/api/exai/assistant/turns", { method: "POST", notifySuccess: false, notifyError: false, signal: controller.signal, data: { conversationId: id, requestId, text: content, modality: isListening ? "voice" : "text", deadlineAt, terminalContext: toMartinaTurnContextPayload({ route: window.location.pathname, ...activeTerminalContext }, id), ...(retry?.assistMode ? { assistMode: retry.assistMode, sourceAssistantMessageId: retry.sourceAssistantMessageId } : {}) } });
          // Se il server ha rimappato lo scope, allinea il drawer alla conversazione
          // dove il messaggio utente e' gia' persistito (anti-loss).
          if (typeof accepted?.conversationId === "string" && accepted.conversationId !== id) {
            sentConversationId = accepted.conversationId;
            activeConversationIdRef.current = accepted.conversationId;
            setConversationId(accepted.conversationId);
            localStorage.setItem(STORAGE_KEY, accepted.conversationId);
          }
        } catch (postError) {
          // Il server puo' aver accettato il job (202) anche se il client ha perso
          // la risposta. Prima di dichiarare fallimento, riprendi per requestId.
          const recovered = await apiRequest(`/api/exai/assistant/turns/${requestId}`, { signal: controller.signal, notifySuccess: false, notifyError: false }).catch(() => null);
          if (!shouldResumeAcceptedTurnAfterPostFailure(recovered)) throw postError;
          accepted = recovered;
        }
      }
      setSegments(composerSegmentsFromPlainText(""));
      setMentionChoices([]);
      setInterim("");
      composerClearedAfterPersist = true;
      activeRequestRef.current = { requestId, runId: accepted.runId, controller };
      sessionStorage.setItem(ACTIVE_RUN_STORAGE_KEY, JSON.stringify({ requestId, runId: accepted.runId, conversationId: sentConversationId ?? id } satisfies PersistedMartinaRun));
      pendingRequestIdRef.current = null;
      const result = await waitForPersistentRun(accepted.runId, controller, 0, deadlineAt);
      await applyPersistentRunResult(sentConversationId ?? id, result);
      sessionStorage.removeItem(ACTIVE_RUN_STORAGE_KEY);
      return true;
    } catch (error) {
      const isCurrentConversation = sentConversationId !== null && activeConversationIdRef.current === sentConversationId;
      if (!controller.signal.aborted && (isCurrentConversation || sentConversationId !== null)) {
        if (shouldClearPersistentRunPointer(error)) sessionStorage.removeItem(ACTIVE_RUN_STORAGE_KEY);
        const details = extractApiErrorDetails(error instanceof Error ? error.message : String(error));
        const history = sentConversationId
          ? await loadMessages(sentConversationId).catch(() => [] as Message[])
          : [];
        const savedUserTurn = history.some((message: Message) => isSavedUserTurnForRequest({
          requestId,
          plainText: visible || content,
          message,
        }));
        if (savedUserTurn) {
          if (!composerClearedAfterPersist) {
            setSegments(composerSegmentsFromPlainText(""));
            setMentionChoices([]);
            setInterim("");
          }
          setError(martinaUiErrorMessage(error, "Il messaggio è in cronologia. Martina non ha ancora completato la risposta: riprova o attendi il credito/provider."));
        } else {
          // Validazione/accept falliti prima del persist: ripristina il testo.
          setSegments(composerSegmentsFromPlainText(visible || content));
          setMentionChoices([]);
          setInterim("");
          if (optimisticUserId) {
            setMessages((current) => current.filter((message) => message.id !== optimisticUserId));
          }
          setError(martinaUiErrorMessage(error, "Il turno non è stato completato. Controlla e riprova: se non compare nella cronologia, il messaggio non è stato registrato."));
        }
        setRetryableError(details.retryable !== false);
      }
      return false;
    }
    finally {
      if (activeRequestRef.current?.requestId === requestId || requestId === "") {
        activeRequestRef.current = null;
        setLoading(false);
      }
    }
  };
  const stopTurn = () => {
    const active = activeRequestRef.current;
    if (!active) return;
    active.controller.abort();
    sessionStorage.removeItem(ACTIVE_RUN_STORAGE_KEY);
    void apiRequest(`/api/exai/assistant/turns/${active.requestId}/cancel`, { method: "POST", notifySuccess: false }).catch(() => undefined);
    setError("Turno interrotto. Nessuna azione proposta è stata applicata.");
    setLoading(false);
  };
  autoSendVoiceRef.current = (transcript) => {
    const generation = voiceSendGenerationRef.current;
    const sourceScope = voiceScope;
    const canApply = () => generation === voiceSendGenerationRef.current
      && sourceScope === voiceScopeRef.current
      && drawerOpenRef.current
      && !ignoreVoiceTranscriptRef.current;
    if (!canApply() || loading || !transcript.trim()) return;
    const query = primaryComposerNameQuery(transcript);
    if (!query) {
      if (canApply()) void send({ text: transcript, append: true });
      return;
    }
    void (async () => {
      try {
        const uniqueness = resolveComposerMentionUniqueness(query, await fetchComposerMentionHits(query));
        if (!canApply()) return;
        const decision = decideVoiceAutoSend({ transcript, uniqueness });
        if (decision.action === "block_ambiguous") {
          if (!canApply()) return;
          setSegments(decision.segments);
          setMentionChoices(decision.candidates);
          return;
        }
        if (decision.action === "block_pending") return;
        if (decision.action === "send") {
          if (!canApply()) return;
          void send({ text: serializeComposerPayload(decision.segments), append: true });
          return;
        }
        if (canApply()) void send({ text: transcript, append: true });
      } catch {
        if (canApply()) void send({ text: transcript, append: true });
      }
    })();
  };
  useEffect(() => {
    // F-005: non auto-inviare finché l'open-effect non ha scelto/escluso la
    // conversazione — altrimenti ensureConversation crea un id che l'hydrate
    // sovrascrive → «contesto cambiato… riprova».
    if (!open || !autoSubmitPrompt || !initialPrompt?.trim() || loading || !conversationScopeReady) return;
    const key = `${activeTerminalContext?.entityType ?? "global"}:${activeTerminalContext?.entityId ?? ""}:${initialPrompt}`;
    // F-007: brucia la key solo dopo successo. In-flight evita doppio fire
    // mentre la race open/ensureConversation è ancora in corso.
    if (autoSubmittedPromptKey.current === key || autoSubmitInFlightKey.current === key) return;
    autoSubmitInFlightKey.current = key;
    void send({ text: initialPrompt, append: true })
      .then((ok) => {
        if (ok) autoSubmittedPromptKey.current = key;
      })
      .finally(() => {
        if (autoSubmitInFlightKey.current === key) autoSubmitInFlightKey.current = null;
      });
  }, [open, autoSubmitPrompt, initialPrompt, activeTerminalContext?.entityType, activeTerminalContext?.entityId, conversationId, conversationScopeReady]);
  const confirmProposal = async (messageId: string, blockIndex: number) => {
    if (loading) return;
    setLoading(true); setError(null);
    try {
      await apiRequest(`/api/exai/assistant/proposals/${messageId}/${blockIndex}/confirm`, { method: "POST", notifyError: false });
      if (conversationId) await loadMessages(conversationId);
    } catch (error) {
      const details = extractApiErrorDetails(error instanceof Error ? error.message : String(error));
      setError(martinaUiErrorMessage(error, "Non riesco ad applicare la proposta. Nessuna modifica parziale è stata dichiarata."));
      setRetryableError(details.retryable !== false);
      if (conversationId) await loadMessages(conversationId).catch(() => undefined);
    }
    finally { setLoading(false); }
  };
  const dismissProposal = async (messageId: string, blockIndex: number) => {
    if (loading) return;
    setLoading(true); setError(null);
    try { await apiRequest(`/api/exai/assistant/proposals/${messageId}/${blockIndex}/dismiss`, { method: "POST", notifyError: false }); if (conversationId) await loadMessages(conversationId); }
    catch { setError("Non riesco ad annullare la proposta."); }
    finally { setLoading(false); }
  };
  const markResultIrrelevant = (itemType: string, itemKey: string) => {
    if (dismissedResultKeys.has(itemKey)) return;
    setDismissedResultKeys((prev) => new Set(prev).add(itemKey));
    apiRequest("/api/exai/assistant/results/feedback", { method: "POST", notifySuccess: false, data: { itemType, decision: "dismissed" } }).catch(() => undefined);
  };
  const beginProposalEdit = (messageId: string, blockIndex: number, proposal: any) => {
    setEditingProposalKey(`${messageId}:${blockIndex}`);
    setProposalDraft({ ...proposal, dueDate: toLocalDateTime(proposal.dueDate), startTime: toLocalDateTime(proposal.startTime), endTime: toLocalDateTime(proposal.endTime) });
  };
  const saveProposalEdit = async (messageId: string, blockIndex: number) => {
    if (!proposalDraft || loading) return;
    setLoading(true); setError(null);
    try {
      const proposal = { ...proposalDraft };
      for (const field of ["dueDate", "startTime", "endTime"]) if (field in proposal) proposal[field] = proposal[field] ? new Date(proposal[field]).toISOString() : null;
      await apiRequest(`/api/exai/assistant/proposals/${messageId}/${blockIndex}`, { method: "PATCH", notifyError: false, data: { proposal } });
      setEditingProposalKey(null); setProposalDraft(null); if (conversationId) await loadMessages(conversationId);
    } catch { setError("Non riesco a salvare la modifica. La proposta originale non è stata applicata."); }
    finally { setLoading(false); }
  };
  const activeScope = conversationScope(activeTerminalContext);
  const switchToGlobalContext = () => {
    if (!onUseGlobalContext || loading) return;
    const draft = displayComposerText(segments).trim();
    if (draft && draft !== initialPrompt?.trim()) {
      setError("La domanda in preparazione appartiene a questa fonte. Inviala o cancellala prima di passare alla domanda globale.");
      return;
    }
    cancelVoiceCapture();
    setSegments(composerSegmentsFromPlainText(""));
    setMentionChoices([]);
    setInterim("");
    setError(null);
    lastSent.current = null;
    onUseGlobalContext();
  };
  const activeConversationMatchesScope = conversationId
    ? conversations.some((conversation) => conversation.id === conversationId && conversationMatchesScope(conversation, activeScope))
    : false;
  const visibleMessages = activeConversationMatchesScope ? messages : [];
  const conversationalVisibleMessages = visibleMessages.filter((message) => message.role === "user" || message.role === "assistant");
  const latestUserMessageId = [...conversationalVisibleMessages].reverse().find((message) => message.role === "user")?.id;
  const latestTurnStartIndex = latestUserMessageId
    ? conversationalVisibleMessages.findIndex((message) => message.id === latestUserMessageId)
    : conversationalVisibleMessages.length - 1;
  const latestTurnMessages = latestTurnStartIndex >= 0 ? conversationalVisibleMessages.slice(latestTurnStartIndex) : [];
  const latestTurnMessageIds = new Set(latestTurnMessages.map((message) => message.id));
  const conversationHistoryCount = conversationalVisibleMessages.length - latestTurnMessages.length;
  const hasPendingOperatorNotice = !open && !loading && !isListening && operatorNotificationCount > 0;
  const launcherStatus = isListening
    ? "In ascolto"
    : loading
      ? "Operativa"
      : hasPendingOperatorNotice
        ? `${operatorNotificationCount > 9 ? '9+' : operatorNotificationCount} priorità operative`
        : "Pronta";

  return <>
    <div className="martina-global-launcher group fixed bottom-20 right-6 z-40 flex border border-primary/30 bg-background shadow-lg md:bottom-6">
      <button type="button" className="flex h-12 items-center gap-2 px-3 text-left hover:bg-muted" onClick={() => setOpen(true)} aria-label={hasPendingOperatorNotice ? `Apri Martina: ${operatorNotificationCount} priorità operative da vedere` : "Apri Martina"}>
        <MartinaAvatar active={loading || isListening} listening={isListening} size="sm" />
        <span className="flex min-w-[78px] flex-col leading-tight">
          <span className="text-sm font-semibold text-foreground">Martina</span>
          <span className={hasPendingOperatorNotice ? "text-[11px] font-medium text-destructive" : "text-[11px] text-muted-foreground"}>{launcherStatus}</span>
        </span>
      </button>
      <Button className="h-12 rounded-none border-l border-primary/20 px-3" variant={isListening ? "default" : "ghost"} onClick={() => { drawerOpenRef.current = true; setOpen(true); if (isSupported) window.setTimeout(() => { if (drawerOpenRef.current) startVoiceCapture(); }, 0); }} aria-label="Detta a Martina" title="Detta a Martina"><Mic className="h-4 w-4" /></Button>
    </div>
    <Sheet open={open} onOpenChange={(nextOpen) => { drawerOpenRef.current = nextOpen; if (!nextOpen) cancelVoiceCapture(); setOpen(nextOpen); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl gap-0 p-0 rounded-none flex h-dvh max-h-dvh min-h-0 flex-col overflow-hidden">
        <SheetHeader className="border-b border-border p-3 pr-12">
          <div className="grid grid-cols-[minmax(0,1fr)] gap-y-1 text-left">
            <SheetTitle className="min-w-0 text-base"><span className="sm:hidden">Martina</span><span className="hidden sm:inline">Martina — Assistente personale CRM</span></SheetTitle>
            <SheetDescription className="line-clamp-1 text-xs">{activeScope.bound ? "Conversazione isolata della fonte · stessa memoria CRM." : "Contesto globale · una sola memoria CRM, conversazioni separate per argomento."}</SheetDescription>
            {!activeScope.bound && <div className="flex min-w-0 items-center gap-2 pt-1">
              <label className="sr-only" htmlFor="martina-global-topic">Argomento Martina</label>
              <select id="martina-global-topic" className="min-h-11 min-w-0 flex-1 border border-border bg-background px-2 text-xs text-foreground" value={startNewGlobalTopic ? "new" : conversationId ?? ""} disabled={loading} onChange={(event) => void selectGlobalTopic(event.target.value)}>
                {startNewGlobalTopic && <option value="new">Nuovo argomento · scrivi la prima domanda</option>}
                {!conversationId && !startNewGlobalTopic && <option value="">Prima domanda</option>}
                {conversations.filter((item) => conversationMatchesScope(item, activeScope)).map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
              </select>
              <Button size="sm" variant="outline" className="min-h-11 shrink-0 rounded-none px-2 text-xs" disabled={loading} onClick={beginNewGlobalTopic}>Nuovo argomento</Button>
            </div>}
          </div>
        </SheetHeader>
        <ActiveContextBar terminalContext={activeTerminalContext} onUseGlobalContext={onUseGlobalContext ? switchToGlobalContext : undefined} />
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 space-y-3 sm:p-4" aria-live="polite" data-testid="martina-conversation">
          {visibleMessages.length === 0 && <div className="border border-border p-4 text-sm text-muted-foreground">Chiedi a Martina cosa ricorda, prepara un follow-up o continua una conversazione precedente.</div>}
          {conversationHistoryCount > 0 && <Button size="sm" variant="outline" className="w-full justify-start rounded-none text-xs" aria-expanded={showConversationHistory} onClick={() => setShowConversationHistory((visible) => !visible)}>{showConversationHistory ? "Nascondi cronologia precedente" : `Mostra cronologia precedente (${conversationHistoryCount})`}</Button>}
          {conversationalVisibleMessages.filter((message) => showConversationHistory || latestTurnMessageIds.has(message.id)).map((message) => <div key={message.id} className={`border border-border p-3 text-sm ${message.role === "user" ? "ml-8 bg-muted" : "mr-8 bg-background"}`}>
            <div className="mb-1 text-xs font-medium text-muted-foreground">{message.role === "user" ? "Tu" : "Martina"}{!latestTurnMessageIds.has(message.id) ? (message.role === "user" ? " · richiesta precedente" : " · risposta precedente, contenuto storico") : ""}{message.status === "failed" ? " · risposta non completata" : ""}</div>
            {message.role === "assistant" ? <MartinaMarkdown text={assistantPlainTextForDisplay(message)} /> : <p className="whitespace-pre-wrap text-foreground">{stripComposerTransportForDisplay(message.plainText) || message.plainText}</p>}
            {message.role === "assistant" && message.contentBlocks?.map((block, blockIndex) => block.type === "provider_escalation" ? <section key={blockIndex} className="mt-3 border-t border-border pt-3">
              <Button
                size="sm"
                variant="outline"
                className="rounded-none gap-1"
                disabled={loading}
                onClick={() => send({
                  text: "Approfondisci con AI la domanda precedente.",
                  displayText: "Approfondisci con AI la domanda precedente.",
                  assistMode: "provider",
                  sourceAssistantMessageId: message.id,
                  requestIdOverride: `provider-assist:${message.id}`,
                  append: true,
                })}
              >
                <Sparkles className="h-3.5 w-3.5" />
                {block.label || "Approfondisci con AI"}
              </Button>
              <p className="mt-1 text-[11px] text-muted-foreground">{block.requiresExternalConsent
                ? "Autorizzi il provider AI esterno configurato a elaborare questa email e questa richiesta. Il consenso vale solo per questo contenuto: niente invio email, bozze o modifiche CRM."
                : "Usa il provider AI configurato e le fonti CRM già raccolte. Non avvia una ricerca online."}</p>
            </section> : block.type === "provider_assistance" && block.status === "completed" ? <div key={blockIndex} className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Sparkles className="h-3 w-3" />Approfondimento AI completato</div> : block.type === "email_reply_options" && block.options?.length ? <section key={blockIndex} className="mt-3 border border-primary p-3">
              <div className="text-xs font-semibold">Come vuoi impostare questa email?</div>
              <p className="mt-1 text-xs text-muted-foreground">Scegli la direzione: Martina scriverà poi una sola bozza completa, usando il thread e le fonti della Stanza.</p>
              <div className="mt-3 grid gap-2">
                {block.options.map((option) => <Button key={option.key} size="sm" variant="outline" className="h-auto justify-start whitespace-normal rounded-none px-3 py-2 text-left" disabled={loading} onClick={() => send({ text: `Prepara la bozza email seguendo questa direzione. Direzione selezionata: ${option.label}. ${option.description}`, append: true })}>
                  <span><span className="font-semibold">{option.label}</span><span className="block text-xs font-normal text-muted-foreground">{option.description}</span></span>
                </Button>)}
              </div>
            </section> : (block.type === "email_reply_draft" || block.type === "email_compose_draft") && block.text ? <section key={blockIndex} className="mt-3 border border-primary p-3">
              <div className="text-xs font-semibold">Bozza {block.mode === "reply_all" ? "Rispondi a tutti" : block.mode === "forward" ? "Inoltra" : block.mode === "compose" ? "Nuova email" : "Rispondi"}{block.language ? ` · ${block.language}` : ""}</div>
              {block.type === "email_compose_draft" && <div className="mt-2 text-xs text-muted-foreground"><div>A: <span className="font-medium text-foreground">{block.recipientLabel || block.to}</span></div><div>Oggetto: <span className="font-medium text-foreground">{block.subject}</span></div></div>}
              {editingEmailDraftKey === `${message.id}:${blockIndex}` ? <div className="mt-3 space-y-2">
                {block.type === "email_compose_draft" && <Input value={emailDraftEdit.subject} onChange={(event) => setEmailDraftEdit((current) => ({ ...current, subject: event.target.value }))} placeholder="Oggetto" maxLength={200} />}
                <Textarea value={emailDraftEdit.text} onChange={(event) => setEmailDraftEdit((current) => ({ ...current, text: event.target.value }))} rows={8} maxLength={20_000} aria-label="Testo della bozza email" />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="rounded-none" disabled={!emailDraftEdit.text.trim() || savingDraftKey === `${message.id}:${blockIndex}`} onClick={() => saveEmailDraft(message, blockIndex)}>{savingDraftKey === `${message.id}:${blockIndex}` ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}Salva bozza</Button>
                  <Button size="sm" variant="outline" className="rounded-none" disabled={savingDraftKey === `${message.id}:${blockIndex}`} onClick={() => setEditingEmailDraftKey(null)}>Annulla modifica</Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Il salvataggio modifica questa bozza senza chiamare il provider AI e senza inviare email.</p>
              </div> : <p className="mt-2 whitespace-pre-wrap text-sm">{block.text}</p>}
              {editingEmailDraftKey !== `${message.id}:${blockIndex}` && <div className="mt-3 flex flex-wrap gap-2">
                {block.status === "sent" ? <EmailSendOutcome delivery={block.delivery} sentAt={block.sentAt} /> : block.status === "superseded" ? <span className="inline-flex h-8 items-center border border-border px-2 text-xs text-muted-foreground">Sostituita da una bozza più recente</span> : block.status === "dismissed" ? <span className="inline-flex h-8 items-center border border-border px-2 text-xs text-muted-foreground">Bozza scartata</span> : draftBelongsToComposer(message) ? <Button size="sm" className="rounded-none gap-1" disabled={loading || reviewingComposer || !onSendFromComposer || !getComposerSendReview} onClick={() => void reviewComposerDraft(message, blockIndex, block)} title="Verifica e conferma l'email aperta nel composer, con allegati e destinatari">
                  <Send className="h-3.5 w-3.5" />
                  Inviala
                </Button> : <Button size="sm" className="rounded-none gap-1" disabled={loading} onClick={() => setPendingEmailSend({ message, index: blockIndex, block, composer: false })} title={block.type === "email_compose_draft" ? "Verifica e conferma questa nuova email" : "Verifica e conferma questa bozza nel thread email"}>
                  {sendingDraftKey === `${message.id}:${blockIndex}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Inviala
                </Button>}
                {(block.status ?? "draft") === "draft" && <Button size="sm" variant="outline" className="rounded-none" disabled={loading} onClick={() => { setEditingEmailDraftKey(`${message.id}:${blockIndex}`); setEmailDraftEdit({ text: block.text!, subject: block.subject ?? "" }); }} title="Modifica e salva questa bozza senza usare il provider AI">
                  Modificala
                </Button>}
                {(block.status ?? "draft") === "draft" && onInsertEmailReply && <Button size="sm" variant="outline" className="rounded-none" disabled={loading} onClick={() => { onInsertEmailReply({ body: block.text!, mode: block.mode ?? "reply", language: block.language }); setOpen(false); }} title="Porta la bozza nel composer della email aperta">Apri nel composer</Button>}
                {(block.status ?? "draft") === "draft" && <Button size="sm" variant="outline" className="rounded-none gap-1" disabled={loading} onClick={() => send({ text: `Rigenera la bozza di risposta per questa email mantenendo lo stesso contesto. Migliora tono, chiarezza e sintesi rispetto alla bozza precedente.`, append: true })} title="Genera una nuova versione usando una singola chiamata al provider AI">
                  <RefreshCw className="h-3.5 w-3.5" />
                  Riscrivila con AI
                </Button>}
                {(block.status ?? "draft") === "draft" && <Button size="sm" variant="ghost" className="rounded-none text-destructive" disabled={savingDraftKey === `${message.id}:${blockIndex}`} onClick={() => dismissEmailDraft(message, blockIndex)} title="Scarta questa bozza senza inviarla"><X className="mr-1 h-3.5 w-3.5" />Scarta</Button>}
              </div>}
            </section> : block.type === "email_send_confirmation" && block.status === "sent" ? <section key={blockIndex} className="mt-3">
              <EmailSendOutcome delivery={block.delivery} sentAt={block.sentAt} />
            </section> : block.type === "result_list" && block.items ? <section key={blockIndex} className="mt-3 border-t border-border pt-3">
              <div className="mb-2 flex items-center justify-between gap-2"><span className="flex items-center gap-1 text-xs font-semibold"><ListFilter className="h-3.5 w-3.5" />{block.title || "Risultati CRM"}</span><span className="flex shrink-0 items-center gap-1">{block.crmQuery && martinaCrmQueryViewPath(block.crmQuery) ? <Button size="sm" variant="ghost" className="h-7 rounded-none gap-1 px-2 text-[11px]" disabled={loading} onClick={() => { const path = martinaCrmQueryViewPath(block.crmQuery!); if (path) { setOpen(false); navigate(path); } }}><ArrowUpRight className="h-3 w-3" />Apri vista</Button> : null}{block.requestText ? <Button size="sm" variant="ghost" className="h-7 rounded-none gap-1 px-2 text-[11px]" disabled={loading} onClick={() => send({ text: block.requestText!, append: true })}><RefreshCw className="h-3 w-3" />Riesegui</Button> : <span className="text-[11px] text-muted-foreground">Salvato nella conversazione</span>}</span></div>
              {block.ambiguity?.status === "candidate_set" && <div role="alert" className="mb-2 border border-amber-500/60 bg-amber-50 p-2 text-xs text-amber-950"><span className="font-semibold">Soggetto ambiguo:</span> Martina non sceglie automaticamente tra {block.ambiguity.candidates.map((candidate) => candidate.title).join(", ")}. Indica il record corretto.</div>}
              {block.ambiguity?.status === "needs_clarification" && <div role="alert" className="mb-2 border border-amber-500/60 bg-amber-50 p-2 text-xs text-amber-950"><span className="font-semibold">Soggetto da precisare:</span> {block.ambiguity.subject}. La ricerca non viene sintetizzata come se fosse verificata.</div>}
              {block.failedEntityTypes?.length ? <div role="status" className="mb-2 border border-amber-500/50 bg-amber-50 p-2 text-xs text-amber-900">Non verificato: {block.failedEntityTypes.join(", ")} non hanno completato la lettura.</div> : null}
              <div className="max-h-80 overflow-y-auto border border-border">
                {block.items.length === 0 ? <div className="p-3 text-xs text-muted-foreground">Nessun record corrispondente. La ricerca resta salvata e può essere affinata nella conversazione.</div> : orderedResultItems(block.items || []).map((item, itemIndex) => { const itemKey = `${item.type}-${item.id}`; const category = resultCategoryFor(item); const relation = resultRelationLabel(item.relation); const dismissed = dismissedResultKeys.has(itemKey); const excerpt = readableResultExcerpt(item.excerpt); const filePresentation = item.type === "file" ? fileResultPresentation(item) : null; const detail = <><div className="flex items-start justify-between gap-2"><span className="font-medium">{item.title}</span>{item.href ? <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : null}</div><div className="mt-0.5 text-xs text-muted-foreground">{filePresentation ? filePresentation.status : <>{resultTypeLabel(item.type)}{relation ? ` · ${relation}` : ""}{item.subtitle ? ` · ${item.subtitle}` : ""}{item.occurredAtLocal ? ` · ${item.occurredAtLocal}` : item.occurredAt ? ` · ${new Intl.DateTimeFormat("it-IT", { timeZone: item.timezone || "UTC", dateStyle: "short", timeStyle: "short" }).format(new Date(item.occurredAt))} (${item.timezone || "UTC"})` : ""}</>}</div>{!filePresentation && item.matchedBy ? <div className="mt-0.5 text-[11px] text-muted-foreground">Verifica: {item.matchedBy === "title_or_structured_relation" ? "titolo o relazione CRM" : "testo o relazione"}</div> : null}{filePresentation?.preview ? <p className="mt-1 line-clamp-4 whitespace-pre-line text-xs text-muted-foreground"><span className="font-medium text-foreground">{filePresentation.status === "Testo estratto consultato" ? "Anteprima dal file: " : "Anteprima da verificare: "}</span>{filePresentation.preview}</p> : !filePresentation && excerpt ? <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">{excerpt}</p> : null}{filePresentation?.note ? <p className="mt-1 text-[11px] text-muted-foreground">{filePresentation.note}</p> : null}</>; return <Fragment key={itemKey}>{(itemIndex === 0 || resultCategoryFor(orderedResultItems(block.items || [])[itemIndex - 1]) !== category) && <div className="border-b border-border bg-muted/50 px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{RESULT_CATEGORY_LABELS[category]}</div>}<div className="group relative border-b border-border last:border-b-0 hover:bg-muted">
                  {item.href ? <a href={item.href} className="block p-2 pr-7">{detail}</a> : <div className="p-2 pr-7">{detail}</div>}
                  {item.sourceHref ? <a href={item.sourceHref} className="block border-t border-border px-2 py-1.5 text-xs text-primary underline">Fonte: {item.sourceTitle || "Email di origine"}</a> : null}
                  <button type="button" title={dismissed ? "Segnalato a Martina come non pertinente" : "Segna come non pertinente per Martina"} aria-label="Segna come non pertinente" disabled={dismissed} className={`absolute right-1 top-1 rounded-none border border-border bg-background p-1 opacity-0 transition-opacity group-hover:opacity-100 ${dismissed ? "text-primary opacity-100" : "text-muted-foreground hover:text-foreground"}`} onClick={(event) => { event.preventDefault(); event.stopPropagation(); markResultIrrelevant(item.type, itemKey); }}>
                    <ThumbsDown className="h-3 w-3" />
                  </button>
                </div></Fragment>; })}
              </div>
            </section> : block.type === "web_sources" && webSourcesForDisplay(block.sources).length ? <section key={blockIndex} className="mt-3 border-t border-border pt-3">
              <div className="mb-2 flex items-center justify-between gap-2"><span className="flex items-center gap-1 text-xs font-semibold"><Globe2 className="h-3.5 w-3.5" />{block.title || "Fonti online"}</span><span className="text-[11px] text-muted-foreground">Web · {webSourcesForDisplay(block.sources).length} fonti</span></div>
              {block.query && <p className="mb-2 text-xs text-muted-foreground">Ricerca: {block.query}</p>}
              <div className="max-h-72 overflow-y-auto border border-border">
                {webSourcesForDisplay(block.sources).map((source) => <a key={source.url} href={source.url} target="_blank" rel="noreferrer" className="block border-b border-border p-2 last:border-b-0 hover:bg-muted">
                  <div className="flex items-start justify-between gap-2"><span className="font-medium">{source.title}</span><ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /></div>
                  {source.citedText && <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">{source.citedText}</p>}
                </a>)}
              </div>
            </section> : block.type === "web_status" && block.status === "failed" ? <section key={blockIndex} className="mt-3 border border-border p-3">
              <div className="flex items-center gap-2 text-xs font-semibold"><Globe2 className="h-3.5 w-3.5" />{block.title || "Ricerca online non completata"}</div>
              <p className="mt-1 text-xs text-muted-foreground">{block.text}</p>
            </section> : block.type === "proposal" && block.proposal ? <section key={blockIndex} className="mt-3 border border-primary p-3">
              <div className="flex items-center gap-2 font-medium"><CalendarDays className="h-4 w-4" />{block.proposal.type === "extract_file" ? "Analisi file proposta" : block.proposal.type === "create_task" ? "Task proposto" : block.proposal.type === "create_meeting" ? "Appuntamento proposto" : block.proposal.type === "create_company" ? "Azienda proposta" : block.proposal.type === "update_task" ? "Modifica Task proposta" : block.proposal.type === "update_meeting" ? "Rischedula appuntamento proposta" : block.proposal.type === "set_attention_lane" ? "Priorità Stanza proposta" : block.proposal.type === "snooze_situation" ? "Rinvio situazione proposto" : block.proposal.type === "resolve_situation" ? "Chiusura situazione proposta" : block.proposal.type === "mark_not_relevant" ? "Archiviazione situazione proposta" : "Opportunità proposta"}</div>
              {editingProposalKey === `${message.id}:${blockIndex}` ? <div className="mt-2 space-y-2">
                <Input aria-label="Titolo proposta" className="rounded-none" value={proposalDraft?.title ?? ""} onChange={(event) => setProposalDraft((current: any) => ({ ...current, title: event.target.value }))} />
                <Textarea aria-label="Descrizione proposta" className="min-h-16 rounded-none" value={proposalDraft?.description ?? ""} onChange={(event) => setProposalDraft((current: any) => ({ ...current, description: event.target.value || null }))} />
                {block.proposal.type === "create_task" || block.proposal.type === "update_task" ? <Input aria-label="Scadenza proposta" type="datetime-local" className="rounded-none" value={proposalDraft?.dueDate ?? ""} onChange={(event) => setProposalDraft((current: any) => ({ ...current, dueDate: event.target.value }))} /> : block.proposal.type === "create_meeting" || block.proposal.type === "update_meeting" ? <div className="grid grid-cols-1 gap-2 sm:grid-cols-2"><Input aria-label="Inizio proposta" type="datetime-local" className="rounded-none" value={proposalDraft?.startTime ?? ""} onChange={(event) => setProposalDraft((current: any) => ({ ...current, startTime: event.target.value }))} /><Input aria-label="Fine proposta" type="datetime-local" className="rounded-none" value={proposalDraft?.endTime ?? ""} onChange={(event) => setProposalDraft((current: any) => ({ ...current, endTime: event.target.value }))} /></div> : null}
                <div className="flex gap-2"><Button size="sm" className="rounded-none" disabled={loading || !proposalDraft?.title?.trim()} onClick={() => saveProposalEdit(message.id, blockIndex)}>Salva modifica</Button><Button size="sm" variant="outline" className="rounded-none" disabled={loading} onClick={() => { setEditingProposalKey(null); setProposalDraft(null); }}>Chiudi</Button></div>
              </div> : <><p className="mt-1">{block.proposal.title}</p><p className="text-xs text-muted-foreground">{block.proposal.type === "extract_file" ? "Estrazione locale di testo/OCR; nessuna analisi prima della conferma" : block.proposal.type === "set_attention_lane" ? `Priorità ${block.proposal.lane}` : block.proposal.type === "snooze_situation" && block.proposal.until ? `Ritorna il ${new Date(block.proposal.until).toLocaleString("it-IT")}` : block.proposal.type === "resolve_situation" ? "Chiude la situazione senza cancellare fonti o storico" : block.proposal.type === "mark_not_relevant" ? "Archivia la situazione senza cancellare fonti o storico" : block.proposal.dueDate ? new Date(block.proposal.dueDate).toLocaleString("it-IT") : block.proposal.startTime ? new Date(block.proposal.startTime).toLocaleString("it-IT") : "Data da definire"}</p>
              {block.origin?.label && <p className="mt-1 text-xs text-muted-foreground">Origine: {block.origin.label}</p>}
              {block.lastError?.message && <div role="alert" className="mt-2 border border-destructive p-2 text-xs text-destructive">Ultimo tentativo non applicato: {block.lastError.message}{block.lastError.code?.startsWith("DEAL_SYNERGY_CLOSEOUT_") && Number.isFinite(block.proposal?.taskId) ? <Button size="sm" variant="outline" className="ml-2 h-7" onClick={() => { setOpen(false); navigate(withReturnTo(`/tasks?taskId=${block.proposal.taskId}`)); }}>Apri attività</Button> : null}</div>}
              {block.status === "applied" ? <div className="mt-2 flex flex-wrap items-center gap-2"><span className="flex items-center gap-1 text-xs text-primary"><Check className="h-3.5 w-3.5" />{block.proposal.type === "extract_file" ? "Richiesta di analisi registrata" : "Applicato nel CRM"}</span>{appliedEntityRoute(block.entity) && <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={() => { setOpen(false); navigate(withReturnTo(appliedEntityRoute(block.entity)!)); }}><ArrowUpRight className="h-3.5 w-3.5" />Apri</Button>}</div> : block.status === "dismissed" ? <div className="mt-2 text-xs text-muted-foreground">Proposta annullata</div> : <div className="mt-2 flex flex-wrap gap-2"><Button size="sm" className="rounded-none" disabled={loading} onClick={() => confirmProposal(message.id, blockIndex)}>{["extract_file", "set_attention_lane", "update_task", "update_meeting", "snooze_situation", "resolve_situation", "mark_not_relevant"].includes(block.proposal.type) ? "Conferma" : "Conferma e crea"}</Button>{!["extract_file", "set_attention_lane", "snooze_situation", "resolve_situation", "mark_not_relevant"].includes(block.proposal.type) && <Button size="sm" variant="outline" className="rounded-none" disabled={loading} onClick={() => beginProposalEdit(message.id, blockIndex, block.proposal)}>Modifica</Button>}<Button size="sm" variant="outline" className="rounded-none" disabled={loading} onClick={() => dismissProposal(message.id, blockIndex)}>Annulla</Button></div>}</>}
            </section> : null)}
            {message.role === "assistant" && <MartinaMessageSources contentBlocks={message.contentBlocks} />}
          </div>)}
          {loading && <MartinaThinkingIndicator
            stage={runPhase === "queued" ? "reading" : runPhase === "crm_deterministic" ? "checking" : runPhase === "provider_synthesis" ? "comparing" : "waiting"}
            message={loadingSeconds >= 20 ? "Martina sta completando la verifica. Potrebbe richiedere ancora qualche secondo…" : undefined}
          />}
          <div ref={messageEndRef} aria-hidden />
        </div>
        <div className="shrink-0 space-y-2 border-t border-border bg-background p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]" data-testid="martina-composer-footer">
          {(error || voiceError) && <div className="flex items-center justify-between gap-2 text-xs text-destructive"><span>{error || voiceError}</span>{error && retryableError && lastSent.current && <Button size="sm" variant="outline" className="rounded-none h-7 gap-1" onClick={() => send({ ...lastSent.current!, append: true })}><RefreshCw className="h-3 w-3" /> Riprova</Button>}</div>}
          <div className="flex gap-1 overflow-x-auto pb-1" aria-label="Richieste rapide Martina"><Button size="sm" variant="outline" className="shrink-0 rounded-none" disabled={loading} onClick={() => { cancelVoiceCapture(); setSegments(composerSegmentsFromPlainText("Preparami il briefing operativo di oggi: cosa è urgente, cosa è scaduto e quali appuntamenti o email richiedono attenzione?")); setMentionChoices([]); }}>Briefing oggi</Button><Button size="sm" variant="outline" className="shrink-0 rounded-none" disabled={loading} onClick={() => { cancelVoiceCapture(); setSegments(composerSegmentsFromPlainText("Mostrami l'agenda di domani con task e appuntamenti in ordine cronologico.")); setMentionChoices([]); }}>Agenda domani</Button><Button size="sm" variant="outline" className="shrink-0 rounded-none" disabled={loading} onClick={() => { cancelVoiceCapture(); setSegments(composerSegmentsFromPlainText(activeScope.bound && activeTerminalContext?.title ? `Cerca sul web informazioni aggiornate su ${activeTerminalContext.title}, distinguile dai dati CRM e mostrami le fonti.` : "Cerca nel CRM aziende, contatti, deal, lead, filiali ed email pertinenti alla mia richiesta; se chiedo dati esterni estendi la ricerca al web con fonti.")); setMentionChoices([]); }}>Ricerca assistita</Button></div>
          <MartinaComposerInlineField
            ref={composerRef}
            segments={segments}
            onSegmentsChange={(next) => {
              cancelVoiceCapture();
              setSegments(next);
              setMentionChoices([]);
            }}
            onCaretChange={setComposerCaret}
            rows={2}
            readOnly={isListening}
            interimSuffix={isListening ? interim : ""}
            placeholder="Chiedi a Martina…"
            ariaLabel="Messaggio per Martina"
            textareaClassName="max-h-64 overflow-y-auto"
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) send();
            }}
          />
          {mentionChoices.length > 0 && <ul role="listbox" aria-label="Scegli il record CRM per il nome digitato" className="max-h-48 overflow-y-auto border border-border bg-background">{mentionChoices.map((hit) => {
            const disambiguation = composerMentionDisambiguationLabel(hit);
            return <li key={`${hit.type}:${hit.id}`}><button type="button" className="flex min-h-11 w-full items-center px-3 py-2 text-left text-sm hover:bg-muted" onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            const uniqueness = uniquenessFromSelectedComposerHit(hit);
            if (!uniqueness) return;
            cancelVoiceCapture();
            const display = displayComposerText(segments);
            const caret = Math.max(0, Math.min(composerCaret, display.length));
            const query = activeComposerAtQuery(segments, caret)?.query.trim() || activeComposerPlainNameQuery(segments, caret)?.text || hit.title;
            setSegments((current) => applyUniqueMentionToSegments(current, query, uniqueness, caret));
            setMentionChoices([]);
          }}><span className="font-medium">{hit.title}</span><span className="ml-2 text-[11px] font-semibold uppercase tracking-wide text-primary">{hit.type}</span>{disambiguation ? <span className="ml-2 text-xs italic text-muted-foreground">{disambiguation}</span> : null}</button></li>;
          })}</ul>}
          {/* Touch target (bug reale 25 lug 2026, verifica mobile Chrome 375px):
              size="sm" del Button e' h-7 (28px), sotto il minimo di 44px
              (Apple HIG / Material). Il microfono con label sr-only diventava
              un'area di 36x28 nell'angolo basso-sinistra del pannello — troppo
              piccola e nella zona dove iOS intercetta i gesti di sistema
              (swipe da bordo sinistro/inferiore). Fix sui bottoni icona vivi
              (Stop/Detta): niente chrome allegato/immagine finche' non c'e'
              handler (F-026). */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-1.5">{loading && <Button size="sm" variant="destructive" className="rounded-none gap-1 h-11" onClick={stopTurn}><Square className="h-3.5 w-3.5" />Stop</Button>}<Button size="sm" variant="outline" className="rounded-none h-11 w-11 p-0" disabled={!isSupported || loading} onClick={() => isListening ? stop() : startVoiceCapture()}><Mic className="h-4 w-4" /> <span className="sr-only">{isListening ? "Ferma dettatura" : "Detta"}</span></Button></div>
            <Button size="sm" className="rounded-none gap-1" disabled={!conversationScopeReady || loading || Boolean(mentionChoices.length) || !displayComposerText(segments).trim()} onClick={() => send()}>{loading || !conversationScopeReady ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {conversationScopeReady ? "Invia" : "Apertura chat…"}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
    <AlertDialog open={Boolean(pendingEmailSend)} onOpenChange={(nextOpen) => { if (!nextOpen) setPendingEmailSend(null); }}>
      <AlertDialogContent className="flex max-h-[90dvh] max-w-lg flex-col overflow-hidden rounded-none">
        <AlertDialogHeader className="shrink-0">
          <AlertDialogTitle>Conferma l'invio dell'email</AlertDialogTitle>
          <AlertDialogDescription>
            Martina ha preparato una bozza. L'email partirà solo dopo il comando «Conferma e invia».
          </AlertDialogDescription>
        </AlertDialogHeader>
        {pendingEmailSend && <div className="min-h-0 flex-1 space-y-2 overflow-y-auto text-sm">
          {pendingEmailSend.review && <p className="break-words"><span className="font-semibold">Da:</span> {pendingEmailSend.review.from}</p>}
          <p className="break-words"><span className="font-semibold">Destinatario:</span> {pendingEmailSend.review ? pendingEmailSend.review.to.join(', ') : pendingEmailSend.block.to || pendingEmailSend.block.recipientLabel || 'quello del thread originale, ricontrollato dal server'}</p>
          {pendingEmailSend.review?.cc.length ? <p className="break-words"><span className="font-semibold">CC:</span> {pendingEmailSend.review.cc.join(', ')}</p> : null}
          {pendingEmailSend.review?.bcc.length ? <p className="break-words"><span className="font-semibold">BCC:</span> {pendingEmailSend.review.bcc.join(', ')}</p> : null}
          <p className="break-words"><span className="font-semibold">Oggetto:</span> {pendingEmailSend.review ? pendingEmailSend.review.subject : pendingEmailSend.block.subject || '(senza oggetto)'}</p>
          <div className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words border border-border bg-muted/30 p-2">{pendingEmailSend.review?.text ?? pendingEmailSend.block.text}</div>
          {pendingEmailSend.review && <p className="break-words text-xs"><span className="font-semibold">Allegati:</span> {pendingEmailSend.review.attachments.join(', ') || 'Nessuno'}</p>}
        </div>}
        <AlertDialogFooter className="shrink-0">
          <AlertDialogCancel className="min-h-11 rounded-none">Torna alla bozza</AlertDialogCancel>
          <AlertDialogAction className="min-h-11 rounded-none" onClick={async () => {
            const pending = pendingEmailSend;
            setPendingEmailSend(null);
            if (!pending) return;
            if (pending.composer) {
              let latest: MartinaEmailComposerReview | null | undefined;
              try { latest = await getComposerSendReview?.(pending.block.text || ''); }
              catch { setError('Non è stato possibile verificare la composizione corrente. Nessun invio eseguito.'); return; }
              if (!pending.review || !latest || latest.fingerprint !== pending.review.fingerprint) {
                setError('La composizione è cambiata. Ricontrolla l’email e conferma di nuovo; nessun invio eseguito.');
                return;
              }
              if (onSendFromComposer?.(pending.block.text || '', pending.review.fingerprint) === true) setOpen(false);
            } else {
              void sendEmailDraft(pending.message, pending.index, pending.block.type as 'email_reply_draft' | 'email_compose_draft');
            }
          }}>Conferma e invia</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;
}
